import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ExecutionResult } from '@apollo/react-common';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { isApolloError } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { retry } from '@confluence/retry';
import {
	EDIT_PAGE_LOAD_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperimentalResetCollabServiceResp } from '@confluence/fabric-providers/entry-points/ResetCollabServiceMutation.ts';
import { BadStatusError } from '@confluence/network';
import { getStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { getTraceIdFromApolloError } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { CollabRecoveryErrorType } from './collabErrors.ts';

const getStatusCodeFromApolloError = (error: ApolloError) => {
	return isApolloError(error) &&
		BadStatusError.isBadStatusError(error?.graphQLErrors?.[0]?.originalError)
		? Number(getStatusCodeError(error!.graphQLErrors[0]!.originalError))
		: null;
};

type NativeCollabRecovery = {
	recover: () => Promise<void>;
	recoveryError: Error | null;
};

/** Provides document recovery with shared retry, concurrency, and error state. */
export const useCollabRecovery = (
	resetCollabService: () => Promise<ExecutionResult<ExperimentalResetCollabServiceResp>>,
	contentId: string,
	contentType: string,
): NativeCollabRecovery => {
	const [recoveryError, setRecoveryError] = useState<Error | null>(null);
	const hasResetRef = useRef({ active: true, resetInProgress: false, resetCount: 0 });
	const experienceTracker = useContext(ExperienceTrackerContext);
	const isLivePage = useIsCurrentPageLive();
	const currentEditorPageLoadExperienceName = isLivePage
		? VIEW_PAGE_EXPERIENCE
		: EDIT_PAGE_LOAD_EXPERIENCE;

	useEffect(() => {
		// Live doc navigation can change the document without unmounting this hook.
		const recoveryState = { active: true, resetInProgress: false, resetCount: 0 };
		hasResetRef.current = recoveryState;
		setRecoveryError(null);
		return () => {
			recoveryState.active = false;
		};
	}, [contentId]);

	const stopExperienceOnRecoveryError = useCallback(
		(recoveryErrorForAnalytics: any) => {
			if (!!recoveryErrorForAnalytics?.status && recoveryErrorForAnalytics?.status < 500) {
				experienceTracker.abort({
					name: currentEditorPageLoadExperienceName,
					reason: `Recovery failed with error status ${recoveryErrorForAnalytics.status}`,
					attributes: {
						errorStatus: recoveryErrorForAnalytics?.status,
						traceId: recoveryErrorForAnalytics?.traceId,
						objectId: contentId,
						objectType: contentType,
						isLivePage,
					},
				});
				return;
			}
			experienceTracker.stopOnError({
				name: currentEditorPageLoadExperienceName,
				error: recoveryErrorForAnalytics,
				attributes: {
					...(recoveryErrorForAnalytics?.status
						? { errorStatus: recoveryErrorForAnalytics.status }
						: {}),
					errorType: 'Recovery failed',
					traceId: recoveryErrorForAnalytics?.traceId,
					objectId: contentId,
					objectType: contentType,
					isLivePage,
				},
			});
		},
		[contentId, contentType, experienceTracker, currentEditorPageLoadExperienceName, isLivePage],
	);

	const recover = useCallback(async () => {
		const recoveryState = hasResetRef.current;
		if (!recoveryState.active || recoveryState.resetInProgress) {
			return;
		} else if (recoveryState.resetCount > 0) {
			// Recovery has already been attempted, but NCS still requires recovery.
			const error = new CollabRecoveryErrorType(`Collab recovery unsuccessful`);
			setRecoveryError(error);
			stopExperienceOnRecoveryError(error);
			return;
		}
		recoveryState.resetInProgress = true;
		recoveryState.resetCount += 1;

		const shouldRetryPredicate = async (error: any): Promise<boolean> => {
			const statusCode = getStatusCodeFromApolloError(error);
			return recoveryState.active && !!statusCode && statusCode >= 500;
		};

		try {
			await retry(
				async () => {
					// Navigation can happen during the retry delay, after the predicate was checked.
					if (recoveryState.active) {
						return resetCollabService();
					}
				},
				{ maxAttempts: 2, timeout: 2000 },
				shouldRetryPredicate,
			);
			recoveryState.resetInProgress = false;
		} catch (e) {
			if (!recoveryState.active) {
				return;
			}
			// page recovery has failed, editor is empty... fatal error
			let recoveryError = e; // "public" error used by other components
			const statusCode = getStatusCodeFromApolloError(e);
			if (!!statusCode) {
				recoveryError = new CollabRecoveryErrorType(
					`Collab recovery responded with ${statusCode}`,
					statusCode,
					getTraceIdFromApolloError(e) ?? undefined,
				);
			}

			setRecoveryError(recoveryError);
			stopExperienceOnRecoveryError(recoveryError);
			recoveryState.resetInProgress = false;
		}
	}, [resetCollabService, stopExperienceOnRecoveryError]);

	return { recover, recoveryError };
};
